import MuiLink, { LinkProps as MuiLinkProps } from '@mui/material/Link'
import { styled } from '@mui/material/styles'
import clsx from 'clsx'
import { get } from 'lodash'
import { AnchorHTMLAttributes, forwardRef } from 'react'
import { Link as RouterLink, useLocation } from 'react-router'

// Add support for the sx prop for consistency with the other branches.
const Anchor = styled('a')({})

interface RouterLinkComposedProps extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> {
  to: string
  replace?: boolean
}

export const NextLinkComposed = forwardRef<HTMLAnchorElement, RouterLinkComposedProps>(
  function NextLinkComposed(props, ref) {
    const { to, replace, ...other } = props

    return <RouterLink ref={ref} replace={replace} to={to} {...other} />
  },
)

export type LinkProps = {
  activeClassName?: string
  as?: string
  href: string | { pathname?: string; query?: Record<string, string> }
  linkAs?: string
  noLinkStyle?: boolean
  external?: boolean
  replace?: boolean
  scroll?: boolean
  shallow?: boolean
  prefetch?: boolean
  legacyBehavior?: boolean
  locale?: string
} & Omit<RouterLinkComposedProps, 'to' | 'href'> &
  Omit<MuiLinkProps, 'href'>

// A styled version of the Link component using React Router
const Link = forwardRef<HTMLAnchorElement, LinkProps>(function Link(props, ref) {
  const {
    activeClassName = 'active',
    as,
    className: classNameProps,
    href,
    legacyBehavior,
    linkAs: linkAsProp,
    locale,
    noLinkStyle,
    prefetch,
    replace,
    scroll,
    shallow,
    external = false,
    ...other
  } = props

  const location = useLocation()

  const pathname = typeof href === 'string' ? href : get(href, 'pathname', undefined)
  const className = clsx(classNameProps, {
    [activeClassName]: location.pathname === pathname && activeClassName,
  })

  const isExternal =
    (typeof href === 'string' && (href.indexOf('http') === 0 || href.indexOf('mailto:') === 0)) ||
    external

  if (
    isExternal &&
    typeof href === 'string' &&
    (href.indexOf('http') === 0 || href.indexOf('mailto:') === 0)
  ) {
    if (noLinkStyle) {
      return <Anchor ref={ref} className={className} href={href} {...other} />
    }

    return (
      <MuiLink
        ref={ref}
        className={className}
        href={href}
        {...other}
        sx={{
          '&.MuiLink-root': {
            color: 'inherit',
          },
        }}
      />
    )
  }

  const to = typeof href === 'string' ? href : (href.pathname ?? '/')

  const routerProps = {
    to,
    replace,
  }

  if (noLinkStyle) {
    return (
      <NextLinkComposed
        ref={ref}
        className={className}
        {...routerProps}
        {...other}
        style={{ textDecoration: 'none' }}
      />
    )
  }

  return (
    <MuiLink
      ref={ref}
      className={className}
      component={NextLinkComposed}
      {...routerProps}
      {...other}
      sx={{
        '&.MuiLink-root': {
          color: 'inherit',
        },
      }}
    />
  )
})

export default Link
